import React, { useEffect, useState } from "react";
import { Sparkles, MessageSquare, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * FloatingAssistant — bouton flottant (FAB) bas-droite qui propose
 * l'assistant IA Pythagore. À la toute première visite (flag
 * `pyth.assistantInvited`), une bulle s'affiche à côté du FAB pour
 * demander explicitement à l'utilisateur s'il souhaite l'essayer.
 * Après acceptation, la bulle ne réapparaît plus. Le FAB reste
 * disponible en permanence.
 *
 * Props :
 *   onOpenChat : () => void — ouvre le ChatSheet existant.
 */
export default function FloatingAssistant({ onOpenChat }) {
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    try {
      const seen = localStorage.getItem("pyth.assistantInvited");
      if (!seen) {
        // Slight delay so the bubble doesn't steal focus from the page
        // content on first paint.
        const t = setTimeout(() => setInviteOpen(true), 1500);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage unavailable — skip invite */ }
  }, []);

  const dismiss = () => {
    setInviteOpen(false);
    try { localStorage.setItem("pyth.assistantInvited", "1"); } catch { /* noop */ }
  };

  const accept = () => {
    dismiss();
    onOpenChat?.();
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-40 flex items-end gap-3"
      data-testid="floating-assistant-root"
    >
      {/* Invitation bubble — première visite uniquement */}
      {inviteOpen && (
        <div
          className="mb-1 max-w-[280px] bg-white border border-zinc-200 rounded-sm shadow-xl p-4 relative animate-fade-in-up"
          data-testid="assistant-invite-bubble"
        >
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-2 right-2 text-zinc-400 hover:text-zinc-600"
            aria-label="Fermer l'invitation"
            data-testid="assistant-invite-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="flex items-start gap-2">
            <div className="shrink-0 h-8 w-8 rounded-sm bg-[#D42B1E]/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-[#D42B1E]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1">
                Assistant IA Pythagore
              </p>
              <p className="text-sm text-zinc-800 leading-relaxed">
                Besoin d'aide pour dimensionner, choisir un SKU ou justifier une recommandation ?
                Voulez-vous l'utiliser ?
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={accept}
                  className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm h-8 text-xs"
                  data-testid="assistant-invite-accept"
                >
                  Oui, essayer
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={dismiss}
                  className="rounded-sm h-8 text-xs text-zinc-500 hover:text-zinc-700"
                  data-testid="assistant-invite-dismiss"
                >
                  Plus tard
                </Button>
              </div>
            </div>
          </div>
          {/* tail pointing to the FAB */}
          <span
            className="absolute -right-1.5 bottom-5 h-3 w-3 bg-white border-r border-b border-zinc-200 rotate-[-45deg]"
            aria-hidden
          />
        </div>
      )}

      {/* FAB principal */}
      <button
        type="button"
        onClick={onOpenChat}
        aria-label="Ouvrir l'assistant IA"
        title="Assistant IA Pythagore"
        data-testid="floating-assistant-fab"
        className="group h-14 w-14 rounded-full bg-[#D42B1E] hover:bg-[#B32218] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 relative"
      >
        <MessageSquare className="h-5 w-5" />
        <Sparkles className="absolute top-1.5 right-1.5 h-3 w-3 text-white/80 animate-pulse" />
        <span className="sr-only">Ouvrir l'assistant IA</span>
      </button>
    </div>
  );
}
